import React, { useCallback, useEffect, useMemo, useState } from "react"

import { MenuItem } from "@mui/material"
import { Formik } from "formik"
import { isEqual } from "lodash"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { ArrowRightIcon } from "@l2r-front/l2r-icons"
import { DEFAULT_SIGN_ICON, MapStyles } from "@l2r-front/l2r-map"
import { BaseNetworkReferentialLayer, useNetworksDispatchContext, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { L2RButton, useIsMobileDevice, useTheme, I18N_NAMESPACE, ScrollableLayout } from "@l2r-front/l2r-ui"
import { findCatalogTypeByCode, getFinalCatalogTypes } from "@l2r-front/l2r-utils"

import { useCatalogItemEditContext } from "../../components/CatalogItemEditModal"
import { useGetMapZoom } from "../../hooks"

import { FormikMapPointEditor } from "../FormikMapPointEditor"

import * as Styled from "./CatalogItemDetailsForm.styled"

const CONTENT_MAX_CHARACTERS = 100

export const CatalogItemDetailsForm = (props: CatalogItemDetailsFormProps) => {

    const {
        addItemDesc,
        catalogTypes,
        className,
        conditions,
        contentFieldLabel,
        disableMaxDistanceCheck = false,
        requirePositionChange = false,
        existingTags,
        forceTextContent = false,
        initialValues,
        onCancel,
        onChange,
        onSubmit,
        typeField,
        typeFieldLabel,
        referential,
        startingPosition,
        startingRoad,
        subNetworks
    } = props

    const [parentType, setParentType] = useState<any>(null)
    const [contextState, contextDispatch] = useCatalogItemEditContext()
    const { typeSelectionDisplayed } = contextState
    const { setTypeSelectionDisplayed, setOnBack } = contextDispatch
    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()
    const theme = useTheme()
    const initialZoom = useGetMapZoom()
    const { setRoadSearched } = useNetworksDispatchContext()
    const FULL_MAP_HEIGHT = 400
    const DETAILS_MAP_HEIGHT = 400

    const showLocation = useMemo(() => !!referential, [referential])
    const showDetails = useMemo(() => !!typeField, [typeField])
    const { selectedNetwork } = useNetworksStateContext()

    const formInitialValues = useMemo(() => ({
        ...initialValues,
        position: startingPosition,
        road: {
            typeid: 1,
            road: startingRoad,
            start: 0,
            end: 1,
        },

    }), [initialValues, startingPosition, startingRoad])

    const conditionOptions = useMemo(() => {
        if (!conditions) {
            return []
        }
        return conditions?.map(condition => <MenuItem
            key={condition.value}
            value={condition.value}>
            {condition.label}
        </MenuItem>)
    }, [conditions])

    const typeOptions = useMemo(() => {
        if (!catalogTypes) {
            return []
        }

        const finalTypes = getFinalCatalogTypes(catalogTypes as any || [])
        return finalTypes.map((type: any) => <MenuItem
            key={type.code}
            value={type.code}>
            {type['name']}
        </MenuItem>)
    }, [catalogTypes])

    const mapCatalogItem = useCallback((item: any): any => ({
        icon: item.icon,
        label: item.name,
        value: item.code,
        children: item.children.map((child: any) => mapCatalogItem(child)),
    }), [])

    const allCatalogTypes = useMemo(
        () => catalogTypes?.map((type: any) => mapCatalogItem(type)),
        [catalogTypes, mapCatalogItem]
    )

    const displayedCatalogTypes = useMemo(() => {
        const types = !parentType ? catalogTypes : parentType.children

        return types?.map((type: any) => mapCatalogItem(type))
    }, [parentType, catalogTypes, mapCatalogItem])

    const catalogTypesListTitle = useMemo(() => (
        parentType ? (parentType as any).name : t(I18N_NAMESPACE, "containers.catalogItemEditForm.category")
    ), [parentType, t])

    interface CompareValuesFields {
        [key: string]: any
        tags?: string[]
    }

    interface CompareValuesParams {
        tags?: string[]
        [key: string]: any
    }

    const compareValues = useCallback(
        (
            values: CompareValuesParams,
            initialValues: CompareValuesParams
        ): boolean => {
            // The road is recomputed by the map editor on mount, it only changes with the position
            const { tags: initialTags, road: _initialRoad, ...initialFields }: CompareValuesFields = initialValues
            const { tags, road: _road, ...fields }: CompareValuesFields = values

            const areInitialTags = Boolean(
                (tags?.length === initialTags?.length) &&
                tags?.every((tag: string) => initialTags?.includes(tag))
            )
            const areInitialFields: boolean = isEqual(fields, initialFields)

            return areInitialFields && (!showDetails || areInitialTags)
        },
        [showDetails]
    )

    const handleFieldChange = useCallback((fieldName: any, value: any, values: any) => {
        const newValues = {
            ...values,
            [fieldName]: value,
        }

        const areInitialValues = compareValues(newValues, formInitialValues)
        onChange(!areInitialValues)
    }, [formInitialValues, compareValues, onChange])

    const handleOpenTypeSelection = useCallback(() => {
        setTypeSelectionDisplayed(true)
    }, [setTypeSelectionDisplayed])

    const handleTypeHierarchyClick = useCallback((code: any, setFieldValue: any, values: any) => {
        const type = findCatalogTypeByCode(code, catalogTypes as any)

        if (type?.children?.length) {
            setParentType(type)
        } else {
            setFieldValue(typeField, code)
            handleFieldChange(typeField, code, values)
            setParentType(null)
            setTypeSelectionDisplayed(false)
            if (!type?.['is_textual'] && !forceTextContent) {
                setFieldValue("content", "")
            }
        }
    }, [catalogTypes,
        forceTextContent,
        typeField,
        setParentType,
        setTypeSelectionDisplayed,
        handleFieldChange,
    ])

    const handleTypeHierarchyCancel = useCallback(() => {
        setParentType(null)
        setTypeSelectionDisplayed(false)
    }, [setParentType, setTypeSelectionDisplayed])

    const onBack = useCallback(() => {
        if (parentType) {
            const parentItemType = findCatalogTypeByCode((parentType as any).parent, catalogTypes as any)
            setParentType(parentItemType)
        } else {
            setTypeSelectionDisplayed(false)
        }
    }, [parentType,
        catalogTypes,
        setParentType,
        setTypeSelectionDisplayed,
    ])

    const handleBack = useCallback(() => {
        setParentType(null)
        setTypeSelectionDisplayed(false)
    }, [setTypeSelectionDisplayed])

    useEffect(() => {
        setOnBack(handleBack)

        return () => {
            setRoadSearched(null)
            setOnBack(null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setOnBack, setRoadSearched])

    const mapHeight = useMemo(() => {
        return isMobile ? "50%" : showDetails ? DETAILS_MAP_HEIGHT : FULL_MAP_HEIGHT
    }, [isMobile, showDetails])

    return <Formik
        initialValues={formInitialValues}
        onSubmit={onSubmit}
    >
        {({ errors, values, setFieldValue }) => {
            const areInitialValues = compareValues(values, formInitialValues)
            const hasErrors = Object.keys(errors).length > 0

            const catalogType = findCatalogTypeByCode((values as any)[typeField], catalogTypes as any || [])
            const isTextual = forceTextContent || catalogType?.['is_textual']

            return !typeSelectionDisplayed ? <Styled.Form>
                <ScrollableLayout
                    className={className}
                    content={
                        <>
                            {showLocation &&
                                <>
                                    <Styled.Desc variant="Desktop & Mobile/Heading/H3">{t(I18N_NAMESPACE, "containers.catalogItemDetailsForm.locationDesc")}</Styled.Desc>
                                    <Styled.MapContainer height={mapHeight}>
                                        <FormikMapPointEditor
                                            disableMaxDistanceCheck={disableMaxDistanceCheck}
                                            positionFieldName="position"
                                            roadFieldName="road"
                                            referential={referential}
                                            initialZoom={initialZoom}
                                            subNetworks={subNetworks}
                                        >
                                            <BaseNetworkReferentialLayer
                                                mapId="catalog-item-details"
                                                mapStyle={MapStyles.satellite}
                                                selectedRoad={values.road.road}
                                                selectedRoadColor={theme.palette["primary"].main}
                                                subNetworks={selectedNetwork?.["children"]?.length ? subNetworks : null}
                                                interactive={false}
                                            />
                                        </FormikMapPointEditor>
                                    </Styled.MapContainer>
                                </>
                            }
                            {showDetails &&
                                <>
                                    {addItemDesc && <Styled.Desc variant="Desktop & Mobile/Heading/H3">{addItemDesc}</Styled.Desc>}
                                    <Styled.Select
                                        id="condition-select"
                                        name="condition"
                                        value={values.condition ?? ""}
                                        label={t(I18N_NAMESPACE, "containers.catalogItemEditForm.condition")}
                                        onChange={(v: any) => handleFieldChange("condition", v, values)}>
                                        {conditionOptions}
                                    </Styled.Select>
                                    <Styled.Select
                                        id="type-select"
                                        IconComponent={ArrowRightIcon}
                                        name={typeField}
                                        value={(values as any)[typeField] ?? ""}
                                        label={typeFieldLabel}
                                        onOpen={handleOpenTypeSelection}>
                                        {typeOptions}
                                    </Styled.Select>
                                    {isTextual && <Styled.TextField
                                        id="content-text"
                                        name="content"
                                        type="text"
                                        value={values.content ?? ""}
                                        slotProps={{ htmlInput: { maxLength: CONTENT_MAX_CHARACTERS } }}
                                        label={contentFieldLabel}
                                        onChange={(e: any) => handleFieldChange("content", e.target.value, values)} />}
                                    <Styled.TagsAutocomplete
                                        name="tags"
                                        variant="outlined"
                                        tags={values.tags}
                                        existingTags={existingTags}
                                        onChange={(v: any) => handleFieldChange("tags", v, values)}
                                    />
                                </>}
                        </>}
                    buttons={
                        <>
                            <L2RButton
                                id="cancel-button"
                                onClick={() => {
                                    onCancel()
                                }}
                                variant="tertiary"
                            >
                                {t(I18N_NAMESPACE, "containers.catalogItemEditForm.cancel")}
                            </L2RButton>
                            <Styled.SubmitButton
                                id="submit-button"
                                type="submit"
                                disabled={hasErrors || areInitialValues || (showDetails && !(values as any)[typeField]) || (requirePositionChange && isEqual(values.position, formInitialValues.position))}
                            >
                                {t(I18N_NAMESPACE, "containers.catalogItemEditForm.save")}
                            </Styled.SubmitButton>
                        </>
                    } />
            </Styled.Form>
                : <Styled.HierarchicalList
                    data={displayedCatalogTypes}
                    searchData={allCatalogTypes}
                    defaultIcon={DEFAULT_SIGN_ICON}
                    handleBack={parentType ? onBack : null}
                    onCancel={handleTypeHierarchyCancel}
                    onClick={(code: any) => handleTypeHierarchyClick(code, setFieldValue, values)}
                    title={catalogTypesListTitle} />
        }}
    </Formik >
}

type CatalogItemDetailsFormProps = {
    className?: string
    forceTextContent?: boolean
    disableMaxDistanceCheck?: boolean
    requirePositionChange?: boolean
    initialValues: {
        condition?: number
        content?: string
        tags?: string[]
        position?: {
            lat: number
            lng: number
        }
        road?: {
            typeid: number
            road?: string
            start: number
            end: number
        }
    }
    catalogTypes: Array<{
        code: string
        name: string
        parent: string
        children: Array<React.ReactNode>
        is_textual?: boolean
    }>
    conditions: Array<{
        label: string
        value: number
    }>
    existingTags: string[]
    addItemDesc?: string
    typeField: string
    typeFieldLabel: string
    contentFieldLabel?: string
    referential?: any
    startingPosition?: {
        lat: number
        lng: number
    }
    startingRoad?: string
    subNetworks?: string[]
    onCancel: () => void
    onChange: (_hasChanged: boolean) => void
    onSubmit: (_values: {
        condition?: number
        content?: string
        tags?: string[]
        position?: {
            lat: number
            lng: number
        }
        road?: {
            typeid: number
            road: string
            start: number
            end: number
        }
    }) => void
}
